import Library from '../utility/Library';
import Reader from './blueprint/Reader';

export default class Card {

	constructor (master, model, no) {

		master.register(this, "card", no);
		this.modelkey = typeof model === 'object' ? model.key : model;
		this.mana = this.model.mana;
		this.properties = [];
		this.variables = {};
		this.statValues = {};
		this.permanentVariables = {};

		if (this.model.blueprint)
			Reader.read(this.model.blueprint, this);

		master.notify('newcard', this, master, model);
	}

	get key () { return this.id.no; }
	get model () { return Library.getUpgradedCard(this.modelkey) }
	get type () { return this.model.type; }
	get subtypes () { return Library.cardSubtypes(this.model); }
	get category () { return this.model.category; }
	get level () { return this.model.level; }
	hasSubtype (subtype) { return Library.hasSubtype(this.model, subtype); }

	get autocast () { return this.getProperty("autocast") }
	get volatile () { return this.getProperty("volatile") || this.hasSubtype("augment") }
	get ethereal () { return this.getProperty("ethereal") }
	get vanish () { return this.getProperty("vanish") || this.hasSubtype("augment") }
	get engine () { return this.hasSubtype("engine") }
	get recovery () { return this.getProperty("recovery") }
	get fullRecoveryActive () { return this.recovery && (this.getVariable("recoveryHandEntries") || 0) <= 1 }
	get recoveryGlowActive () { return this.fullRecoveryActive && this.location === this.master.hand }
	get corrupt () { return this.getProperty("corrupt") }
	get fullCorruptActive () { return this.corrupt && !this.getVariable("corruptUses") }
	get corruptGlowActive () { return this.fullCorruptActive && this.location === this.master.hand }
	get locked () { return this.getProperty("locked") }
	get overload () { return this.getProperty("overload") }
	get overloaded () { return this.getProperty("overloaded") }
	get burning () { return this.getProperty("burning") }
	get allManaCost () { return this.getProperty("allManaCost") }
	get manaCostDivisor () { return this.model.manaCostDivisor }
	get manaOnlyCost () { return this.allManaCost || this.manaCostDivisor }

	reset () {

		this.mana = this.model.mana;
		let overloaded = this.overloaded;
		this.properties = [];
		this.manashift = 0;
		if (overloaded)
			this.properties.push("overloaded");
		this.variables = {};
		delete this.extratriggers;
	}

	getProperty (property) {

		if (property === "locked" && this.location === this.master.hand) {
			if (this.isFrozenByFreeze())
				return true;
		}
		if (this.model[property])
			return true;
		if (Library.hasSubtype(this.model, property))
			return true;
		if (this.properties.includes(property))
			return true;
		if (this.master.player.statuses)
			return this.master.player.statuses.some(status => {
				if (!status.model.modifiers)
					return false;
				return status.model.modifiers.some(modifier => modifier.stat === this.type && modifier.value === property)
			})
		return false;
	}

	isFrozenByFreeze () {

		if (this.engine || this.location !== this.master.hand)
			return false;
		const handIndex = this.master.hand.cards.indexOf(this);
		return handIndex >= 0 && this.master.player.getStatusValue("freeze") > handIndex;
	}

	isFirstNonFrozenInHand () {

		if (this.location !== this.master.hand)
			return false;
		return this.master.hand.cards.find(card => !card.isFrozenByFreeze()) === this;
	}

	get cost () {

		if (this.allManaCost)
			return this.playCost !== undefined ? this.playCost : Math.max(0, this.master.mana);
		if (this.manaCostDivisor)
			return this.playCost !== undefined ? this.playCost : Math.ceil(Math.max(0, this.master.mana) / this.manaCostDivisor);
		if (!this.engine && this.mana === undefined)
			return undefined;
		let cost = this.engine ? (this.master.enginesPlayed || 0) : this.mana;
		if (this.reduced)
			cost -= 1;
		if (this.manashift)
			cost += this.manashift;
		if (this.model.edgeDiscount && this.isFirstNonFrozenInHand())
			cost -= this.model.edgeDiscount;
		cost = Math.max(cost, 0);
		if (this.master.player.statuses)
			this.master.player.statuses.forEach(status => {
				if (!status.model.modifiers)
					return;
				status.model.modifiers.filter(modifier => modifier.stat === "mana" || (modifier.stat === this.type && !isNaN(modifier.value))).forEach(modifier => cost += modifier.value * status.value);
			})
		if (this.model.costStatus)
			cost += this.master.player.getStatusValue(this.model.costStatus);
		return Math.max(0, cost);
	}

	goto (location) {

		if (this.ethereal && location === this.master.discard)
			location = this.master.exile;

		if (this.location === location)
			return;

		let former = this.location;
		this.master.notify("movecard.before", this, former, location);

		this.location = location;
		if (former && former.hasCard(this))
			former.removeCard(this);
		if (location === this.master.discard || (location === this.master.deck && former !== this.master.supply) || (location === this.master.supply && former !== this.master.discard))
			this.reset();
		if (location && !location.hasCard(this))
			location.addCard(this);
		if (location === this.master.hand && this.recovery)
			this.permanentVariables.recoveryHandEntries = (this.getVariable("recoveryHandEntries") || 0) + 1;

		this.master.notify("movecard", this, former, location);
	}

	get canBePlayed () {

		if (this.location !== this.master.hand)
			return false;
		if (this.cost === undefined)
			return false;
		if (this.cost > this.master.mana)
			return false;
		if (this.master.choosingHand)
			return false;
		if (this.locked || this.overloaded)
			return false;
		return true;
	}

	play (target, pay=true, autocast=false) {

		const hand = this.master.hand.cards;
		let playIndex = hand.indexOf(this);
		this.fromLeft = this.isFirstNonFrozenInHand();
		this.fromRight = playIndex === this.master.hand.count-1;
		this.adjLeft = playIndex > 0 ? hand[playIndex-1] : null;
		this.adjRight = playIndex >= 0 && !this.fromRight ? hand[playIndex+1] : null;
		let rightFlag = false;
		hand.forEach(card => {
		  card.leftFromPlay = false;
		  card.rightFromPlay = false;
		  card.adjLeftFromPlay = false;
		  card.adjRightFromPlay = false;
		});
		hand.forEach((card,i) => {
			if (card === this) {
				rightFlag = true;
				if (i - 1 >= 0)
					hand[i - 1].adjLeftFromPlay = true;
				if (i + 1 < hand.length)
					hand[i + 1].adjRightFromPlay = true;
			} else {
				card.leftFromPlay = !rightFlag;
				card.rightFromPlay = rightFlag;
			}
		})
		let paycost = this.cost;
		this.playCost = paycost;
		this.goto(this.master.court);
		if (pay)
			this.master.pay(paycost, this.engine, this.manaOnlyCost);
		if (autocast)
			this.properties.push("autocast");
		this.master.notify("cast.before", this, target, this.autocast);
		const corruptActive = this.fullCorruptActive;
		if (this.events) {
			for (let i = 1; i <= (this.extratriggers || 0); i++) {
				let tmpac = this.properties.autocast;
				if (!tmpac)
					this.properties.push("autocast");
				if (this.canTarget(target)) {
					this.events.forEach(e => e(target));
					this.master.notify("wait", this.master, 300);
				}
				if (!tmpac)
					this.properties = this.properties.filter(p => p !== "autocast");
			}
			if (this.canTarget(target))
				this.events.forEach(e => e(target));
		}
		if (corruptActive)
			this.master.run.champion.applyCurse("corruption");
		if (this.corrupt)
			this.setVariable("corruptUses", (this.getVariable("corruptUses") || 0) + 1, true);
		this.master.notify("cast", this, target, this.autocast);
		if (this.overload)
			this.addProperty("overloaded");
		if (this.vanish)
			this.goto(this.master.exile);
		else if (this.location === this.master.court)
			this.goto(this.master.discard);
		delete this.fromLeft;
		delete this.fromRight;
		delete this.adjLeft;
		delete this.adjRight;
		delete this.playCost;
	}

	canTarget (target) {

		if (this.hasTarget && !target)
			return false;
		/*if (target && target.hp <= 0)
			return false;*/
		if (target && target.hasStatus("hidden"))
			return false;
		return true;
	}

	get targets () {

		var targets = [];
		this.events.forEach(e => {
			if (e.requirement) 
				targets.push(e.requirement);
		});
		return targets;
	}

	setVariable (name, value, permanent=false) {

		if (permanent) {
			this.permanentVariables[name] = value;
			this.master.notify('setpvariable', this, name, value);
			return;
		}
		this.variables[name] = value;
	}

	getVariable (name) {

		return this.variables[name] !== undefined ? this.variables[name] : this.permanentVariables[name];
	}

	clearVariable (name) {

		if (this.variables[name])
			delete this.variables[name];
		if (this.permanentVariables[name])
			delete this.permanentVariables[name];
		this.master.notify('clearpvariable', this, name);
	}

	getStatValue (name) {

		if (this.model.key === "climax" && name === "times")
			return this.modifyTensionDamageRate(this.model.times);
		if (this.model.key === "hot-shot") {
			if (name === "value2")
				return this.modifyTensionDamageRate(this.model.value2);
			if (name === "value")
				return this.modifyHotShotTensionCap();
		}
		let value = this.statValues[name] || this.model[name];
		if (this.isAnimalSpiritManaLeafRefundStat(name) && value !== undefined)
			value += this.animalSpiritManaLeafRefundBonus();
		return value;
	}

	isAnimalSpiritManaLeafRefundStat (name) {

		if (this.model.key === "spirit-of-the-owl")
			return name === "value2";
		return this.model.category === "spirit" && name === "value";
	}

	animalSpiritManaLeafRefundBonus () {

		if (this.model.category !== "spirit")
			return 0;
		const relic = this.master?.run?.champion?.relics?.find(relic => relic.key === "spiritual-statuette");
		return relic ? 2 : 0;
	}

	recoveryAmount () {

		const log = this.master.run?.broadcaster?.log || [];
		let lastTurn = 0;
		let currentTurn = 0;
		log.forEach(entry => {
			if (entry.type === "startfight") {
				lastTurn = 0;
				currentTurn = 0;
			} else if (entry.type === "playerturn") {
				lastTurn = currentTurn;
				currentTurn = 0;
			}
			if (entry.type === "lifeloss" && this.isChampionLifeLossTarget(entry.data?.[0]))
				currentTurn += entry.data[2] || 0;
			if (entry.type === "recoveryheal")
				currentTurn -= entry.data?.[1] || 0;
		});
		return Math.max(0, lastTurn + currentTurn);
	}

	championLostLifeLastTurn () {

		const log = this.master.run?.broadcaster?.log || this.master.run.log || [];
		let lostLife = false;
		let currentTurnLostLife = false;
		log.forEach(entry => {
			if (entry.type === "startfight") {
				lostLife = false;
				currentTurnLostLife = false;
			} else if (entry.type === "playerturn") {
				lostLife = currentTurnLostLife;
				currentTurnLostLife = false;
			}
			if (entry.type === "lifeloss" && this.isChampionLifeLossTarget(entry.data?.[0]) && (entry.data?.[2] || 0) > 0)
				currentTurnLostLife = true;
		});
		return lostLife;
	}

	isChampionLifeLossTarget (target) {

		return target === "player" || target === "champion" || target === "object" || target?.type === "player";
	}

	get retaliateGlowActive () {

		return this.model.key === "retaliate" && this.location === this.master.hand && this.championLostLifeLastTurn();
	}

	recoveryHealAmount (amount, target=this.master.player) {

		if (this.recovery && !this.fullRecoveryActive && target === this.master.player)
			return Math.min(amount, this.recoveryAmount());
		return amount;
	}

	modifyTensionSpendCost (amount) {

		let dashing = this.master.player.getStatusValue("dashing") + this.master.player.getStatusValue("boosting");
		if (!dashing)
			return amount;
		return Math.ceil(amount * (1 + dashing * 0.25));
	}

	modifyTensionSpend (amount) {

		return amount < 0 ? -this.modifyTensionSpendCost(-amount) : this.modifyTensionSpendCost(amount);
	}

	ignoresTensionSpendModifiers () {

		return ["edge", "never-empty", "spark-of-resolve"].includes(this.model.key);
	}

	tensionRateDivisor () {

		return 4 + this.master.player.getStatusValue("dashing") + this.master.player.getStatusValue("boosting");
	}

	modifyTensionDamageRate (amount) {

		return Math.floor(amount * 4 / this.tensionRateDivisor());
	}

	modifyHotShotTensionCap () {

		const damageRate = this.modifyTensionDamageRate(this.model.value2);
		if (damageRate <= 0)
			return 0;
		return Math.floor(this.model.value * this.model.value2 / damageRate);
	}

	spendsTension () {

		return /spend .*?\[Tension\]/i.test(this.model.effect || "");
	}

	spendsAllTension () {

		return /spend your \[Tension\]/i.test(this.model.effect || "");
	}

	spendsUpToTension () {

		return /spend up to .*?\[Tension\]/i.test(this.model.effect || "");
	}

	setStatValue (name, value) {

		this.statValues[name] = value;
		this.master.notify('statvalue', this, name, value);
	}

	changeMana (value) {

		if (value === this.mana || this.mana === null || this.mana === undefined)
			return;
		this.mana = value;
		this.master.notify("changemana", this, value);
	}

	shiftMana (value) {

		if (value === 0 || (value < 0 && this.mana === 0))
			return;
		this.changeMana(Math.max(this.mana + value, 0));
	}

	tempShiftMana (value) {

		this.manashift = (this.manashift || 0) + value;
		this.master.notify("manashift", this, value);
	}

	addProperty (prop) {

		this.properties.push(prop);
		this.master.notify("addproperty", this, prop);
	}

	removeProperty (prop) {

		this.properties = this.properties.filter(p => p !== prop);
		this.master.notify("removeproperty", this, prop);
	}

	discard () {

		this.goto(this.master.discard);
		this.master.notify("discard", this);
		if (this.master.turn === "player" && this.evanescence)
			this.evanescence();
	}

	exile () {

		this.goto(this.master.exile);
		this.master.notify("exile", this);
	}

	serialize () {

		return {
			key: this.key,
			model: this.modelkey,
			mana: this.mana,
			properties: this.properties,
			manashift: this.manashift,
			extratriggers: this.extratriggers,
			permanentVariables: this.permanentVariables,
			statValues: this.statValues,
			starter: this.starter
		}
	}

	static build (master, src) {

		let card = new Card(master, src.model);
		card.mana = src.mana;
		card.properties = src.properties;
		card.manashift = src.manashift;
		card.extratriggers = src.extratriggers;
		card.permanentVariables = src.permanentVariables;
		card.statValues = src.statValues;
		card.starter = src.starter;
		return card;
	}
}
